import { ChevronDown, ChevronRight, Menu, X } from "lucide-react";
import React, { useState } from "react";
import { getSubdomain } from "../config/configUtils";
import CartIcon from "./CartIcon";
import PhotoModal from "./Photo/Modal";

function FloatingControls({ scrollToSection, visibleSection }) {
  const [openMenu, setOpenMenu] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [expandedCategory, setExpandedCategory] = useState(null); // Track expanded category

  const openModal = () => setIsOpen(true);
  const closeModal = () => setIsOpen(false);
  const toggleMobileMenu = () => setIsMobileMenuOpen(!isMobileMenuOpen);
  const toggleCategory = (category) =>
    setExpandedCategory((prev) => (prev === category ? null : category));

  const categoryLabels = {
    Breakfast: "Breakfast - 2 pax",
    Starters: "Starters - 2 pax",
    Sandwiches: "Sandwiches - 2 pax",
    Caviar: "Caviar",
    "Snack Trays": "Snack Trays - 2 pax",
    "Main Courses": "Main Courses - Individual",
    Desserts: "Desserts - To share",
    "Package - 2 Pax": "Package - 2 Pax",
    "Package - 4 Pax": "Package - 4 Pax",
    "Package - 6 Pax": "Package - 6 Pax",
    "Package - 8 Pax": "Package - 8 Pax",
    "Soft Drinks": "Soft Drinks",
    Water: "Water",
    "Fruit Juice": "Fruit Juice",
    Wines: "Wine - Rose",
    Champagne: "Champagne - Brut",
    Beer: "Beers",
    Liquor: "Liquor",
    Spirit: "Tequila",
  };

  const subdomain = getSubdomain();
  let categories = {};
  if (subdomain === "h1g") {
    categories = {
      food: [
        "Breakfast",
        "Starters",
        "Sandwiches",
        "Caviar",
        "Snack Trays",
        "Main Courses",
        "Desserts",
      ],
      package: [
        "Package - 2 Pax",
        "Package - 4 Pax",
        "Package - 6 Pax",
        "Package - 8 Pax",
      ],
    };
  } else {
    categories = {
      food: [
        "Breakfast",
        "Starters",
        "Sandwiches",
        "Caviar",
        "Snack Trays",
        "Main Courses",
        "Desserts",
      ],
      package: [
        "Package - 2 Pax",
        "Package - 4 Pax",
        "Package - 6 Pax",
        "Package - 8 Pax",
      ],
      drink: [
        "Soft Drinks",
        "Water",
        "Fruit Juice",
        "Wines",
        "Champagne",
        "Beer",
        "Liquor",
        "Spirit",
      ],
    };
  }

  return (
    <>
      {/* Desktop Navbar */}
      <div className="sticky top-0 left-0 w-full bg-primary shadow-md z-50">
        <div className="hidden md:flex items-center justify-between px-6 py-2 relative">
          {/* Centered Left-Side Menu */}
          <div className="absolute left-1/2 transform -translate-x-1/2 flex gap-6">
            {Object.keys(categories).map((section) => (
              <div key={section} className="relative group">
                <button
                  onClick={() => {
                    scrollToSection(section);
                    setOpenMenu(null);
                  }}
                  className={`px-4 py-2 rounded transition hover:text-secondary ${
                    visibleSection === section ? "text-secondary" : "text-white"
                  }`}
                  onMouseEnter={() => setOpenMenu(section)}
                >
                  {section.charAt(0).toUpperCase() + section.slice(1)}
                </button>
                {openMenu === section && categories[section].length > 0 && (
                  <div
                    className="fixed top-[52px] left-1/2 transform -translate-x-1/2 w-screen max-w-screen bg-white p-4 z-40"
                    onMouseLeave={() => setOpenMenu(null)}
                  >
                    <div className="flex flex-wrap gap-4 justify-center align-middle">
                      {categories[section].map((displayName, index) => (
                        <button
                          key={index}
                          onClick={() => {
                            scrollToSection(
                              categoryLabels[displayName] || displayName
                            );
                            setOpenMenu(null);
                          }}
                          className="px-4 py-2 bg-white rounded hover:text-secondary transition"
                        >
                          {displayName}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
            <button
              onClick={openModal}
              className="px-4 py-2 rounded transition text-white hover:text-secondary"
            >
              Gallery
            </button>
          </div>

          {/* Right-Side Cart Icon */}
          <div className="ml-auto">
            <button
              onClick={() => scrollToSection("order")}
              className={`px-4 py-2 transition ${
                visibleSection === "order" ? "text-secondary" : "text-white"
              }`}
            >
              <CartIcon
                className={` ${
                  visibleSection === "order" ? "fill-secondary" : "fill-white"
                } hover:fill-secondary`}
              />
            </button>
          </div>
        </div>

        {/* Mobile Burger Menu */}
        <div className="md:hidden flex justify-between items-center px-4 py-2">
          <button onClick={toggleMobileMenu} className="text-white text-2xl">
            <Menu />
          </button>
          <button
            onClick={() => scrollToSection("order")}
            className={`px-4 pt-[8px] transition ${
              visibleSection === "order" ? "text-secondary" : "text-white"
            }`}
          >
            <CartIcon
              className={`${
                visibleSection === "order" ? "fill-secondary" : "fill-white"
              } hover:fill-secondary`}
            />
          </button>
        </div>
      </div>

      {/* Background Overlay */}
      <div
        className={`fixed inset-0 bg-black bg-opacity-50 transition-opacity ${
          isMobileMenuOpen
            ? "opacity-100 pointer-events-auto"
            : "opacity-0 pointer-events-none"
        } z-40`}
        onClick={toggleMobileMenu}
      ></div>

      {/* Mobile Sliding Menu */}
      <div
        className={`fixed top-0 left-0 h-screen w-64 bg-primary text-white transform ${
          isMobileMenuOpen ? "translate-x-0" : "-translate-x-full"
        } transition-transform duration-300 ease-in-out z-50 overflow-y-auto`}
      >
        <div className="flex justify-between items-center px-4 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold">Menu</h2>
          <button onClick={toggleMobileMenu} className="text-white text-2xl">
            <X />
          </button>
        </div>

        <nav className="p-4 space-y-4">
          {Object.keys(categories).map((section) => (
            <div key={section} className="mb-2">
              <button
                className="flex justify-between items-center w-full text-left px-4 py-2 font-medium text-gray-100 hover:bg-gray-800 rounded"
                onClick={() => toggleCategory(section)}
              >
                <span>{section.toUpperCase()}</span>
                {expandedCategory === section ? (
                  <ChevronDown />
                ) : (
                  <ChevronRight />
                )}
              </button>
              {/* Subcategories */}
              {expandedCategory === section && (
                <div className="ml-4 mt-2 border-l border-gray-300 pl-4 space-y-2">
                  {categories[section].map((subcategory, index) => (
                    <button
                      key={index}
                      onClick={() => {
                        scrollToSection(
                          categoryLabels[subcategory] || subcategory
                        );
                        toggleMobileMenu(); // Close menu after selection
                      }}
                      className="block w-full text-left px-4 py-2 text-gray-300 hover:bg-gray-800 rounded"
                    >
                      {subcategory}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
          <button
            onClick={() => {
              openModal();
              toggleMobileMenu(); // Close menu after selection
            }}
            className="flex justify-between items-center w-full text-left px-4 py-2 font-medium text-gray-100 hover:bg-gray-800 rounded"
          >
            <span>GALLERY</span>
          </button>
        </nav>
      </div>

      {/* Modal for Gallery */}
      <PhotoModal isOpen={isOpen} closeModal={closeModal} />
    </>
  );
}

export default FloatingControls;
